/**
 * Sidebar interno da pagina /chat (M8). Lista threads ``type=chat`` do
 * usuario logado (mais recentes primeiro), com botao "Nova conversa" no
 * topo e delete inline no hover. NAO eh o sidebar global do app.
 *
 * Suporta dois modos:
 *  - Expandido (default): 288px, lista cheia + botao "Nova conversa".
 *  - Recolhido: largura minima (~40px) com so um botao "+" e um botao
 *    para reabrir. Persiste em ``uiStore.chatHistoryCollapsed``.
 */
import { ChevronLeft, ChevronRight, MessageCircle, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useChatThreads,
  useDeleteChatThread,
} from "@/features/chat/hooks/useChatThreads";
import { useUiStore } from "@/stores/uiStore";

export function ChatHistorySidebar({
  activeThreadId,
}: {
  activeThreadId?: string;
}) {
  const { t } = useTranslation("directChat");
  const navigate = useNavigate();
  const threads = useChatThreads();
  const del = useDeleteChatThread();
  const collapsed = useUiStore((s) => s.chatHistoryCollapsed);
  const toggle = useUiStore((s) => s.toggleChatHistory);

  async function handleDelete(e: React.MouseEvent, id: string) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await del.mutateAsync(id);
      toast.success(t("history.deleteThread"));
      if (activeThreadId === id) navigate("/chat");
    } catch (err) {
      toastApiError(err);
    }
  }

  if (collapsed) {
    return (
      <aside className="flex h-full w-12 shrink-0 flex-col items-center gap-2 border-r bg-card py-3">
        <Button
          size="icon"
          variant="ghost"
          onClick={toggle}
          aria-label={t("history.expand", "Expandir histórico")}
          title={t("history.expand", "Expandir histórico")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => navigate("/chat")}
          aria-label={t("newConversation")}
          title={t("newConversation")}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </aside>
    );
  }

  return (
    <aside className="flex h-full w-72 shrink-0 flex-col border-r bg-card">
      <div className="flex items-center gap-2 border-b p-3">
        <Button
          className="flex-1 justify-start gap-2"
          variant="outline"
          onClick={() => navigate("/chat")}
        >
          <Plus className="h-4 w-4" />
          {t("newConversation")}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={toggle}
          aria-label={t("history.collapse", "Recolher histórico")}
          title={t("history.collapse", "Recolher histórico")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <div className="mb-2 px-2 text-xs font-semibold uppercase text-muted-foreground">
          {t("history.title")}
        </div>
        {threads.isLoading ? (
          <div className="px-2 text-xs text-muted-foreground">…</div>
        ) : !threads.data || threads.data.length === 0 ? (
          <div className="px-2 text-xs text-muted-foreground">
            {t("noConversationsYet")}
          </div>
        ) : (
          <ul className="space-y-0.5">
            {threads.data.map((thread) => (
              <li key={thread.id}>
                <NavLink
                  to={`/chat/${thread.id}`}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent",
                      (isActive || activeThreadId === thread.id) &&
                        "bg-accent",
                    )
                  }
                  title={thread.title ?? undefined}
                >
                  <MessageCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">
                    {thread.title || t("history.untitled")}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100"
                    onClick={(e) => handleDelete(e, thread.id)}
                    aria-label={t("history.deleteThread")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
